/**
 * Fee planning for the UTXO manager's split/consolidate flow.
 *
 * The problem this solves: we have to decide how many sats to put in the
 * user's outputs, and everything else — the network fee, plus whatever
 * outputs the API appends server-side — has to come out of the same input
 * total. Get it wrong high and the builder rejects the whole transaction
 * with an insufficient-funds error on a wallet that is perfectly solvent.
 *
 * The rule this module is built on: NEVER assume what the builder will do.
 * The API appends a tool-fee output today; tomorrow it may append an
 * OP_RETURN, change the fee amount, add a second output, or drop the fee
 * entirely — all without the UI being redeployed. So the only load-bearing
 * inputs here are things we measure from the builder's own response:
 *
 *   - a successful build tells us the true fee (inputs - outputs) and how
 *     much came back to us as change;
 *   - a rejected build tells us the exact deficit, and BDK's `needed`
 *     figure already accounts for every output the server added plus the
 *     real fee for the real transaction shape.
 *
 * Everything else in here — the vbyte tables, the server-overhead hint — is
 * a starting guess that only affects how many round trips we take. If those
 * guesses are wrong the answer is still correct; it just costs an extra
 * call. That is the property that makes this safe to leave alone while the
 * builder changes underneath it.
 */

/** Builder response. Only the PSBT is load-bearing; we read the rest off it. */
interface PlanBuildResult {
	psbt: string
}

/**
 * Requests a build of exactly these outputs. Must reject with an Error whose
 * `message` carries the server's text, so a rejection stating the deficit can
 * be read back. Any other rejection is treated as fatal.
 */
export type PlanBuildFn = (outputs: [string, number][]) => Promise<PlanBuildResult>

export interface PlanSplitOptions {
	/** Sum of the inputs being spent. */
	totalValue: number
	/** How many inputs — used only to seed the first guess. */
	inputCount: number
	/** How many outputs the user asked for. */
	numOutputs: number
	/** Where the user's outputs go (may equal `from`). */
	toAddress: string
	/** The address being spent from — also where the builder returns change. */
	from: string
	feeRate: number
	/** Dust threshold for this chain (546 on BTC). */
	dustLimit: number
	build: PlanBuildFn
	/** Network fee of a built PSBT, i.e. inputs - outputs. */
	psbtFee: (psbt: string) => number
	/** Total value of a PSBT's outputs paying `address`. */
	psbtOutputValueTo: (psbt: string, address: string) => number
	/** Round-trip ceiling. Correctness does not depend on this. */
	maxAttempts?: number
}

export interface PlanSplitResult {
	/** The PSBT to sign. Always from a build that actually succeeded. */
	psbt: string
	/** Value in the user's outputs. Corresponds to `psbt`, never a later guess. */
	outputValue: number
	/** True network fee, read off `psbt`. */
	networkFee: number
	/** Everything else the builder took — derived, not assumed. Never negative. */
	serviceFee: number
	/** Builder calls made. Exposed for tests and telemetry. */
	attempts: number
}

/**
 * Per-input vbytes by script type, for the opening guess only.
 *
 * Deliberately above the textbook figures: BDK prices an input off
 * `max_satisfaction_weight`, which assumes a worst-case signature. A P2WPKH
 * input measures ~69.3 vB against the live API, not the nominal 68.
 */
export function perInputVbytes(addr: string): number {
	if (/^(bc1p|tb1p|bcrt1p)/.test(addr)) return 61 // P2TR key-path (~57.5)
	if (/^(bc1|tb1|bcrt1)/.test(addr)) return addr.length > 44 ? 110 : 72 // P2WSH : P2WPKH
	if (/^[32]/.test(addr)) return 95 // P2SH-P2WPKH
	return 152 // P2PKH / legacy
}

/** Per-output vbytes by script type, for the opening guess only. */
export function perOutputVbytes(addr: string): number {
	if (/^(bc1p|tb1p|bcrt1p)/.test(addr)) return 43 // P2TR
	if (/^(bc1|tb1|bcrt1)/.test(addr)) return addr.length > 44 ? 43 : 31 // P2WSH : P2WPKH
	if (/^[32]/.test(addr)) return 32 // P2SH
	return 34 // P2PKH
}

/**
 * What we guess the builder will take on top of the network fee.
 *
 * Today that is the API's 2500-sat INSCRIBE_BASE_FEE output. This is a
 * ROUND-TRIP HINT AND NOTHING MORE — it is never used to compute a number
 * the user sees, and if the API changes it (or stops appending it, or adds
 * an OP_RETURN alongside it) the plan still converges on the correct answer
 * via the builder's own responses. Do not add logic that depends on it.
 */
const SERVER_OVERHEAD_HINT = 2500

const DEFICIT_RE = /insufficient funds:\s*(\d+)\s*sat available of\s*(\d+)\s*sat needed/i

/**
 * Reads the exact shortfall out of a builder rejection.
 *
 * This is the mechanism that makes the planner robust to builder changes:
 * BDK's `needed` figure is computed from the transaction it actually tried
 * to build, so it already includes every server-appended output and the real
 * fee. Returns null when the message isn't a shortfall we can act on.
 */
export function parseDeficit(message: unknown): number | null {
	const match = DEFICIT_RE.exec(String(message ?? ''))
	if (!match) return null
	const deficit = Number(match[2]) - Number(match[1])
	return Number.isFinite(deficit) && deficit > 0 ? deficit : null
}

/** Splits a total across n outputs, giving any remainder to the first. */
export function splitOutputs(
	toAddress: string,
	total: number,
	numOutputs: number
): [string, number][] {
	const per = Math.floor(total / numOutputs)
	const remainder = total - per * numOutputs
	return new Array(numOutputs)
		.fill(null)
		.map((_, i): [string, number] => [toAddress, per + (i === 0 ? remainder : 0)])
}

/**
 * Opening guess at everything that is not the user's outputs.
 *
 * The dust padding at the end is deliberate. The builder only emits a change
 * output when the leftover clears the dust threshold; below that it folds the
 * remainder into the fee, where we can neither see it nor give it back.
 * Reserving one extra dust means an over-generous guess comes back as a real
 * change output that the loop below returns to the user — so guessing high
 * costs a round trip rather than costing the user sats.
 */
export function seedReserve(opts: {
	inputCount: number
	numOutputs: number
	toAddress: string
	from: string
	feeRate: number
	dustLimit: number
}): number {
	const vbytes =
		opts.inputCount * perInputVbytes(opts.from) +
		opts.numOutputs * perOutputVbytes(opts.toAddress) +
		perOutputVbytes(opts.from) + // change
		31 + // server-appended fee output
		11 // tx overhead
	return Math.ceil(vbytes * opts.feeRate) + SERVER_OVERHEAD_HINT + opts.dustLimit
}

export class InsufficientFundsError extends Error {
	constructor(
		message: string,
		readonly shortBy: number | null
	) {
		super(message)
		this.name = 'InsufficientFundsError'
	}
}

/**
 * Finds the largest output value the builder will actually accept.
 *
 * Converges from both directions and is guaranteed to terminate:
 *
 *   grow  — a successful build that returned change means we under-asked, so
 *           we claim the change and retry;
 *   shrink— a rejection tells us the exact deficit, so we give back precisely
 *           that much and retry.
 *
 * Once we have shrunk we never grow again, which is what rules out
 * oscillation if change and a server-appended output are ever confused for
 * one another (they can be, when `from` is itself the fee address). The
 * returned figures always come from a build that succeeded — the output value
 * is never a guess that outran the PSBT it is reported against.
 */
export async function planSplit(opts: PlanSplitOptions): Promise<PlanSplitResult> {
	const {
		totalValue,
		numOutputs,
		toAddress,
		from,
		dustLimit,
		build,
		psbtFee,
		psbtOutputValueTo,
		maxAttempts = 6
	} = opts

	const floor = dustLimit * numOutputs
	if (totalValue <= floor) {
		throw new InsufficientFundsError(
			`Not enough balance to fund ${numOutputs} output${numOutputs !== 1 ? 's' : ''} — ` +
				`select more UTXOs or use fewer outputs`,
			floor - totalValue
		)
	}

	let outputValue = Math.max(floor, totalValue - seedReserve(opts))
	let best: { psbt: string; outputValue: number } | null = null
	let shrunk = false
	let attempts = 0
	let lastError: unknown = null

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		let result: PlanBuildResult
		attempts++
		try {
			result = await build(splitOutputs(toAddress, outputValue, numOutputs))
		} catch (err) {
			lastError = err
			const deficit = parseDeficit((err as any)?.message)

			// Not a shortfall we can reason about (network error, validation,
			// a message format we don't recognise). If we already hold a build
			// that worked, ship that rather than failing the user.
			if (deficit === null) {
				if (best) break
				throw err
			}

			const next = outputValue - deficit
			if (next < floor) {
				if (best) break
				throw new InsufficientFundsError(
					`Not enough balance to cover fees at this rate — need ~${(
						floor - next
					).toLocaleString()} more sats`,
					floor - next
				)
			}

			outputValue = next
			shrunk = true
			continue
		}

		best = { psbt: result.psbt, outputValue }

		// Correcting downward already landed us on the builder's own limit;
		// growing again from here is what would loop forever.
		if (shrunk) break

		// Anything paying `from` beyond the outputs we asked for is change we
		// under-claimed. When `from` is also the fee address this over-counts,
		// and the next build rejects — which the shrink path then corrects
		// exactly. That is why this is allowed to be approximate.
		const paidToFrom = psbtOutputValueTo(result.psbt, from)
		const change = paidToFrom - (toAddress === from ? outputValue : 0)

		if (change > 0 && attempt < maxAttempts - 1) {
			outputValue += change
			continue
		}
		break
	}

	if (!best) {
		if (lastError) throw lastError
		throw new InsufficientFundsError('Could not build transaction', null)
	}

	const networkFee = psbtFee(best.psbt)
	return {
		psbt: best.psbt,
		outputValue: best.outputValue,
		networkFee,
		// Whatever the builder took beyond the fee and the user's outputs.
		// Derived from the PSBT, so it stays honest no matter what the API
		// appends. Clamped because a PSBT we failed to parse reads as fee 0.
		serviceFee: Math.max(0, totalValue - best.outputValue - networkFee),
		attempts
	}
}
